/*
 * Browser application entrypoint: bootstraps page services, routing, navigation and the personal
 * inbox.
 * Used by the HTML shell through the Docker-built application bundle.
 * Load once per browser page; the router owns view cleanup, authentication requirements and
 * stream interests.
 * Example: window.navigate(`/chapters/${chapterId}`);
 */

import { registerAppShell } from './components/navigation/app-shell.jsx';
import { PageChromeHeader } from './components/navigation/page-header.jsx';
import { createInboxSource } from './components/navigation/inbox-source.js';
import { createChapterActivityRuntime } from './streaming/chapter-activity.js';
import { createUIContext } from './runtime/context.js';
import { createNotifications } from './runtime/notifications.js';
import { isAuthCancelled } from './runtime/auth.js';
import { installAuthRecovery } from './components/accounts/auth-recovery.js';
import {
  routeRequiresAuth,
  resourceRequiresAuth,
  loadPageAuthRequirement
} from './lib/ui/auth-policy.js';
import { pickContrastingText } from './lib/ui/color.js';
import { render, nothing } from 'lit-html';
import { createLifetime } from './runtime/lifetime.js';
import {
  setAccountLink as setDocumentAccountLink,
  clearAccountLink as clearDocumentAccountLink,
  clearContinuumLink as clearDocumentContinuumLink
} from './lib/theming/custom-css.js';
import { createURLs } from './lib/ui/urls.js';
import { createPageTitle } from './lib/ui/page-title.js';
import { createConsent } from './components/accounts/coc-modal.js';
import { startLayoutViewportSync } from './lib/ui/layout-viewport.js';
import { createServices } from './runtime/services.js';
import { admireAvailableForPath } from './lib/theming/admire-policy.js';

export const context = createUIContext({ document, window });
createServices(context);
registerAppShell(context.window.customElements);
const { accountStorage, getStorageAccountId, onStorageAccountChange } = context.services.storage;
const { alertDialog } = context.services.dialogs;
const { savePreference, isCurrentPreference } = context.services.preferences;
const { onUIChange } = context.services.sync;
const { transitionContent } = context.services.transitions;
const {
  getUser,
  getToken,
  updateUser,
  bootAuth,
  needsEmailVerification,
  subscribeAuth,
  getAuthPhase,
  getAuthContext,
  getAuthOwner,
  getAuthStorageReady,
  beginAuthPage,
  setPageAuthRequired,
  requireAuthentication,
  assertAuthRecovery
} = context.services.auth;
const { closeModals, focusElement, hasOpenModal } = context.services.a11y;
const { get } = context.services.api;
const {
  setInterests: streamSetInterests,
  start: startStream,
  stop: stopStream
} = context.services.stream;
const { setBodyCover, getCoverVersion, cancelPendingBodyCover } = context.services.covers;
const { hydrate: hydrateReads } = context.services.reads;
const { beginThemeNavigation, finishThemeNavigation, setThemeRoute } = context.services.theme;
const { initImageReporting } = context.services.imageReports;
const { resetReportMode } = context.services.reportMode;
const { resolveContinuumHost, setContinuumHostContext, continuumHref, getCanonicalURL } =
  context.service(createURLs);
const { resetPageTitle } = context.service(createPageTitle);
const { loadCodeOfConduct, showCodeOfConductModal } = context.service(createConsent);
const setAccountLink = (id) => setDocumentAccountLink(id, context.document);
const clearAccountLink = () => clearDocumentAccountLink(context.document);
const clearContinuumLink = () => clearDocumentContinuumLink(context.document);
context.services.chapterActivity = context.service(createChapterActivityRuntime);
context.services.notifications = context.service(createNotifications);
context.own(
  startLayoutViewportSync(context.window, context.document.documentElement, transitionContent)
);

let shell = null;
let inboxSource = null;

async function maybeShowCoC() {
  const u = getUser();
  if (!u || needsEmailVerification()) return;
  try {
    const doc = await loadCodeOfConduct();
    if (doc?.version > 0 && (u.accepted_coc_version || 0) < doc.version) {
      await showCodeOfConductModal();
    }
  } catch {
    // CoC endpoint failure shouldn't block boot — server gate will catch
    // unaccepted users on their next API call and the api.js hook will pop
    // the modal then.
  }
}

// Resolved continuum when we're on a configured hostname. null on TBC host (or when
// the hostname doesn't resolve).
let hostContext = null;
let appReady = false;
let preferenceAccount = getStorageAccountId();
onStorageAccountChange((userId, reason) => {
  if (reason !== 'account' || preferenceAccount === userId) return;
  preferenceAccount = userId;
  setAdmireMode(false);
  if (!appReady) return;

  const generation = routeGeneration;
  queueMicrotask(async () => {
    await getAuthStorageReady();
    if (
      generation === routeGeneration &&
      userId === getStorageAccountId() &&
      getAuthPhase() !== 'recovering'
    )
      void route();
  });
});

let wasUnverified = needsEmailVerification();
subscribeAuth(() => {
  if (getAuthPhase() === 'recovering') return;
  const unverified = needsEmailVerification();
  if (unverified === wasUnverified) return;
  wasUnverified = unverified;
  if (appReady && getToken() && window.location.pathname !== '/me')
    queueMicrotask(() => window.navigate('/me', { replace: true }));
});

export function getHostContext() {
  return hostContext;
}

// Routes that may render content owned by a continuum. Each of these views is
// expected to call setContinuumLink once it knows its continuum context. Any
// route NOT in this set causes the continuum stylesheet link to be removed on
// navigation.
const CONTINUUM_SCOPED_ROUTES = new Set([
  'continuum',
  'chapter',
  'character',
  'story',
  'location',
  'map',
  'page'
]);

let currentUnmount = null;
context.onDispose(() => currentUnmount?.());
let accountMenuCleanup = null;
let popupFocusReturn = null;

// Interest tracking — app.js merges page + popup before calling stream.setInterests
let pageInterests = [];
let popupInterests = [];

function syncInterests() {
  streamSetInterests([...pageInterests, ...popupInterests]);
}

function setPageInterests(interests) {
  pageInterests = interests;
  syncInterests();
}

function currentPopup() {
  return shell?.disposed ? null : shell?.popup;
}

function popupElement() {
  return currentPopup() && shell.popupNode;
}

function closeConversationPopup() {
  const popup = currentPopup();
  if (!popup) return;
  if (popupElement()?.contains(document.activeElement) && !hasOpenModal())
    popupFocusReturn = popup.opener;
  shell.popupNode?.dispose();
  popupInterests = [];
  syncInterests();
  shell.popup = null;
}

function openConversationPopup(conversationId, options = {}) {
  if (!shell || shell.disposed || context.signal.aborted) return;
  const previous = currentPopup();
  const active =
    popupElement()?.contains(document.activeElement) && !hasOpenModal()
      ? previous.opener
      : document.activeElement;
  closeConversationPopup();
  popupFocusReturn = null;
  const hiddenInboxItem =
    active?.closest('#msg-menu') && !document.querySelector('tbc-inbox-menu')?.open;
  const opener =
    active?.closest('.hidden, [hidden], [inert]') || hiddenInboxItem
      ? document.getElementById('msg-menu-button')
      : active;
  const popup = {
    session: {},
    conversationId,
    to: options.to,
    opener,

    setInterests(interests) {
      if (currentPopup() !== popup) return;
      popupInterests = interests;
      syncInterests();
    }
  };
  shell.popup = popup;
}

function closeMessageDialog(session) {
  if (shell?.messageDialog?.session !== session) return;
  shell.dialogNode?.dispose();
  shell.messageDialog = null;
}

async function startChat(group, signal) {
  if (signal.aborted || shell.disposed) return;
  const previous = shell.messageDialog;
  if (previous) closeMessageDialog(previous.session);
  const session = {};
  shell.messageDialog = {
    session,
    kind: group ? 'group' : 'dm',
    callerSignal: signal,
    logicalOwner: document.getElementById('msg-menu-button'),
    close: () => closeMessageDialog(session)
  };
  await shell.whenRendered();
  if (shell.messageDialog?.session === session) return shell.dialogNode;
}

// Open a "new DM" popup chat with the given user. Surfaced on window so any
// view (members table, participants menu, etc.) can call it without importing.
window.openNewDMPopup = function (toUserID) {
  openConversationPopup('new', { to: toUserID });
};
// Switch the existing popup to an already-existing conversation. Used by the
// new-DM flow after first send so the popup keeps the user in the chat
// instead of navigating the whole page.
window.openConversationPopup = openConversationPopup;

/** @param {string} path @param {{ replace?: boolean }} [options] */
window.navigate = function (path, { replace = false } = {}) {
  // Cross-continuum guard: on a hostname, navigating to a different
  // continuum's id-path bumps the user back to the TBC host form of that URL.
  if (hostContext && typeof path === 'string') {
    const m = path.match(/^\/continuums\/([^/?#]+)(.*)$/);
    if (m && m[1] !== hostContext.id) {
      const canonical = getCanonicalURL();
      if (canonical) {
        window.location.href = `${canonical}/continuums/${m[1]}${m[2] || ''}`;
        return;
      }
    }
  }
  if (!path.includes('#')) {
    const pathPart = path.split('?')[0];
    if (pathPart === window.location.pathname) {
      path += window.location.hash;
    }
  }
  history[replace ? 'replaceState' : 'pushState']({}, '', path);
  route();
};

let routeGeneration = 0;
let currentRouteClass = null;

function setRouteContext(main, name) {
  // The router owns the page class; views may replace their own className.
  // Keep body context separate so themes can also reach floating surfaces.
  if (currentRouteClass) main.classList.remove(currentRouteClass);
  currentRouteClass = name ? `route-${name}` : null;
  shell.routeClass = currentRouteClass;
  if (currentRouteClass) {
    main.classList.add(currentRouteClass);
    document.body.dataset.route = name;
  } else {
    delete document.body.dataset.route;
  }
}

async function route({ isCurrent = () => true } = {}) {
  const generation = ++routeGeneration;
  cancelPendingBodyCover();
  await transitionContent(() => (isCurrent() ? renderRoute(generation) : undefined), {
    animate: !!document.getElementById('main-content')?.firstElementChild
  });
}

async function renderRoute(generation) {
  // A second navigation can arrive before the browser captures the first.
  if (generation !== routeGeneration) return;
  const requestedPath = window.location.pathname;
  const needsLogin =
    routeRequiresAuth(requestedPath) || (requestedPath === '/' && hostContext?.authRequired);
  if (getAuthPhase() === 'recovering') closeConversationPopup();
  beginAuthPage(needsLogin);
  shell.popupNode?.route();
  if (needsLogin && !getUser()) {
    const next = window.location.pathname + window.location.search + window.location.hash;
    history.replaceState(null, '', `/login?next=${encodeURIComponent(next)}`);
    hostContext = hostContext?.authRequired ? null : hostContext;
    setPageAuthRequired(false);
  }
  if (
    needsEmailVerification() &&
    !['/me', '/verify-email', '/reset-password', '/reset-password-request'].includes(
      window.location.pathname
    )
  ) {
    history.replaceState(null, '', '/me');
  }

  const path = window.location.pathname;
  const params = new URLSearchParams(window.location.search);
  admireAllowed = admireAvailableForPath(path);
  setAdmireMode(false);
  shell.mapStage = false;
  resetReportMode();

  beginThemeNavigation();
  await shell.park();
  if (generation !== routeGeneration) return;
  currentUnmount?.();
  setPageInterests([]);
  const routeScope = createLifetime();
  currentUnmount = routeScope.dispose;
  resetPageTitle();

  // Snapshot the cover-version so we can tell, after the new view has
  // finished mounting, whether it set its own cover. If it didn't, we
  // clear what the previous view left behind. Doing the check post-mount
  // means entity→entity navigations crossfade directly instead of
  // flashing through "no cover" in between.
  const coverVersionBefore = getCoverVersion();

  const main = document.getElementById('main-content');
  setRouteContext(main, null);
  main.innerHTML = '';

  let viewRoot;
  function createRouteRoot(tag = 'div') {
    const element = document.createElement(tag);
    routeScope.own(() => {
      element.dispose?.();
      element.remove();
    });
    main.append(element);
    viewRoot = element;
    return element;
  }

  renderNav();

  // Route matching
  let viewModule;
  let routeParams = {};

  const rootView = hostContext
    ? [
        /^\/$/,
        () => import('./views/community/continuum.jsx'),
        [],
        'continuum',
        () => ({ continuumId: hostContext.id })
      ]
    : [/^\/$/, () => import('./views/community/lobby.jsx'), [], 'lobby'];

  const routes = [
    rootView,
    [/^\/help\/dice$/, () => import('./views/utilities/dice-help.jsx'), [], 'dice-help'],
    [/^\/login$/, () => import('./views/account/login.jsx'), [], 'login'],
    [/^\/register$/, () => import('./views/account/register.jsx'), [], 'register'],
    [/^\/verify-email$/, () => import('./views/account/verify.jsx'), [], 'verify'],

    [
      /^\/reset-password-request$/,
      () => import('./views/account/reset-password-request.jsx'),
      [],
      'reset-password-request'
    ],

    [
      /^\/reset-password$/,
      () => import('./views/account/reset-password.jsx'),
      [],
      'reset-password'
    ],
    [/^\/me\/preferences$/, () => import('./views/account/preferences.jsx'), [], 'preferences'],
    [/^\/me\/library$/, () => import('./views/community/library.jsx'), [], 'library'],
    [/^\/me$/, () => import('./views/account/index.jsx'), [], 'account'],
    [/^\/messages$/, () => import('./views/messaging/messages-list.jsx'), [], 'messages'],

    [
      /^\/messages\/([^/]+)$/,
      () => import('./views/messaging/conversation.jsx'),
      ['conversationId'],
      'conversation'
    ],

    [
      /^\/continuums\/(?!new$)([^/]+)$/,
      () => import('./views/community/continuum.jsx'),
      ['continuumId'],
      'continuum'
    ],

    [/^\/stories\/([^/]+)$/, () => import('./views/narrative/story.jsx'), ['storyId'], 'story'],

    [
      /^\/characters\/([^/]+)$/,
      () => import('./views/world/character.jsx'),
      ['characterId'],
      'character'
    ],

    [
      /^\/chapters\/([^/]+)$/,
      () => import('./views/narrative/chapter.jsx'),
      ['chapterId'],
      'chapter'
    ],

    [/^\/pages\/([^/]+)$/, () => import('./views/world/page.jsx'), ['pageId'], 'page'],

    [
      /^\/locations\/([^/]+)$/,
      () => import('./views/world/location.jsx'),
      ['locationId'],
      'location'
    ],

    [/^\/maps\/([^/]+)$/, () => import('./views/world/map.jsx'), ['mapId'], 'map'],
    [
      /^\/import$/,
      () =>
        params.get('source') === 'ares'
          ? import('./views/utilities/ares-import.jsx')
          : import('./views/utilities/import.jsx'),
      [],
      'import'
    ],
    [/^\/admin$/, () => import('./views/admin.jsx'), [], 'admin']
  ];

  let routeName;
  for (const [pattern, loader, paramNames = [], name, paramOverrides] of routes) {
    const m = path.match(pattern);
    if (m) {
      paramNames.forEach((p, i) => {
        routeParams[p] = m[i + 1];
      });
      if (paramOverrides) Object.assign(routeParams, paramOverrides());
      routeName = name;
      setThemeRoute(routeName);
      setRouteContext(main, routeName);
      try {
        viewModule = await loader();
      } catch (err) {
        if (generation !== routeGeneration) return;

        // esbuild bundles each route into a content-hashed chunk under
        // /app/js/chunks/<name>-<hash>.js. When a deploy ships fresh
        // chunk hashes but the browser is still running a cached app.js
        // that points at the old names, the dynamic import 404s. Force a
        // full reload to grab the matching entry+chunk pair. Once is enough
        // because the new app.js will reference the new hashes; the sentinel
        // in sessionStorage guards against an infinite loop if something
        // else is broken.
        const RELOAD_KEY = 'chunk_reload_attempt';
        if (!sessionStorage.getItem(RELOAD_KEY)) {
          sessionStorage.setItem(RELOAD_KEY, '1');
          window.location.reload();
          return;
        }
        sessionStorage.removeItem(RELOAD_KEY);
        setThemeRoute('not-found');
        setRouteContext(main, 'not-found');
        createRouteRoot().className = 'view-not-found';

        render(
          <div class="not-found-body">
            {PageChromeHeader({
              heading: (
                <h2 class="not-found-heading" tabindex="-1">Couldn't load this page</h2>
              )
            })}
            <p class="not-found-subtext">
              A stale browser cache may be the cause. Try a hard reload (Cmd/Ctrl+Shift+R).
            </p>
          </div>,
          viewRoot,
          { host: shell, creationScope: document }
        );

        routeScope.own(() => render(nothing, viewRoot));
        viewRoot.querySelector('h2')?.focus({ preventScroll: true });
        console.error('route loader failed', err);
        clearContinuumLink();
        setBodyCover(null);
        finishThemeNavigation();
        shell.resume();
        await shell.whenRendered();
        return;
      }
      if (generation !== routeGeneration) return;
      break;
    }
  }

  if (!viewModule) {
    setThemeRoute('not-found');
    setRouteContext(main, 'not-found');
    createRouteRoot().className = 'view-not-found';

    render(
      <div class="not-found-body">
        {PageChromeHeader({
          heading: (
            <h2 class="not-found-heading" tabindex="-1">Page not found</h2>
          )
        })}
        <p class="not-found-subtext">
          <a href="/" class="link">Go home</a>
        </p>
      </div>,
      viewRoot,
      { host: shell, creationScope: document }
    );

    routeScope.own(() => render(nothing, viewRoot));
    viewRoot.querySelector('h2')?.focus({ preventScroll: true });
    clearContinuumLink();
    setBodyCover(null);
    finishThemeNavigation();
    shell.resume();
    await shell.whenRendered();
    return;
  }

  if (viewModule.componentTag) viewModule.registerComponents(window.customElements);
  createRouteRoot(viewModule.componentTag);

  if (!CONTINUUM_SCOPED_ROUTES.has(routeName)) {
    clearContinuumLink();
  }

  // Initial log positioning needs the saved cursor before a view creates its
  // read tracker. Inbox reconciliation also loads reads, but waits for menus.
  if (getToken() && !needsEmailVerification()) await hydrateReads();
  if (generation !== routeGeneration) return;

  try {
    await routeScope.ensure('view', () => {
      return viewModule.mount(viewRoot, {
        context,
        scope: routeScope,
        params: routeParams,
        query: params,

        setMapStageActive(value) {
          if (generation === routeGeneration && !routeScope.disposed && shell.mapStage !== !!value)
            shell.mapStage = !!value;
        },

        setViewClass(value) {
          if (generation === routeGeneration && !routeScope.disposed) viewRoot.className = value;
        },

        setAuthRequired: (required) => {
          if (generation === routeGeneration) setPageAuthRequired(required);
        },

        setInterests: (interests) => {
          if (generation === routeGeneration) setPageInterests(interests);
        }
      });
    });
    await viewRoot.whenRendered?.();
  } catch (error) {
    const cancelled =
      error?.name === 'AbortError' &&
      (generation !== routeGeneration || routeScope.disposed || context.signal.aborted);

    try {
      routeScope.dispose();
    } catch (cleanup) {
      throw new AggregateError([error, cleanup], 'Route setup and cleanup failed.');
    }
    if (cancelled) return;
    throw error;
  } finally {
    if (generation === routeGeneration) {
      finishThemeNavigation();
      shell.resume();
      await shell.whenRendered();
    }
  }
  if (generation !== routeGeneration) {
    routeScope.dispose();
    return;
  }

  // The new view didn't set a cover — fade whatever the previous view left
  // up out, so cover-less routes (auth, etc.) don't
  // inherit the wrong art.
  if (getCoverVersion() === coverVersionBefore) {
    setBodyCover(null);
  }

  // Move keyboard focus into the new view so screen-reader users hear the
  // page change — but only if the view didn't already focus something itself
  // (login focuses the username field, etc.). Prefer the view's primary
  // heading; fall back to <main>.
  const focusedInside = document.activeElement && viewRoot.contains(document.activeElement);
  if (!focusedInside && !hasOpenModal()) {
    for (const target of [...viewRoot.querySelectorAll('h1, h2'), main]) {
      if (focusElement(target)) break;
    }
  }
}

function loadConversations() {
  return inboxSource?.refresh() || Promise.resolve();
}

window.addEventListener('focus', () => {
  if (getToken() && needsEmailVerification()) {
    get('/api/me')
      .then((user) => {
        if (user.id === getUser()?.id) updateUser(user);
      })
      .catch(() => {});
  }
});

onUIChange((change, remote) => {
  if (isCurrentPreference(change) && change.key === 'content_expanded') renderViewControls();
  if (remote && change.kind === 'account' && change.user?.id === getUser()?.id) {
    updateUser(change.user);
    refreshAccountMenuIdentity();
  }
});

function applyAccountStylesheet() {
  const user = getUser();
  if (user?.id) setAccountLink(user.id);
}

function accountMenuDestination(path = window.location.pathname) {
  if (path === '/me/preferences') return '/me/preferences';
  if (path === '/me/library') return '/me/library';
  return path === '/me' ? '/me' : '';
}

function refreshAccountMenuIdentity() {
  const user = getUser();
  if (!user) return;
  if (shell)
    shell.navigation = {
      ...shell.navigation,
      accountLabel: user.username || 'Account',
      accountEmail: user.email || ''
    };
}

window.addEventListener('account-identity-changed', refreshAccountMenuIdentity);

function renderNav() {
  try {
    renderNavContents();
  } finally {
    renderViewControls();
  }
}

function clearNavigationResources() {
  try {
    accountMenuCleanup?.();
  } finally {
    accountMenuCleanup = null;
  }
}

function replaceNavigation(state, actions) {
  const navigation =
    state.kind === 'member'
      ? {
          source: inboxSource,
          openConversation: openConversationPopup,

          leftConversation(id) {
            if (currentPopup()?.conversationId === id) closeConversationPopup();
            if (window.location.pathname === `/messages/${id}`) window.navigate('/messages');
          },

          startChat
        }
      : {};
  const publicLogin =
    state.kind === 'anonymous'
      ? {
          context,
          onLogin: state.onLogin,
          refresh: refreshPublicLoginPage,
          onError: (error) => context.services.dialogs.alertDialog(error.message)
        }
      : null;
  clearNavigationResources();
  shell.navigationNode?.querySelector('tbc-inbox-menu')?.close();
  shell.navigationNode?.querySelector('tbc-reviews-menu')?.close();
  const input = { ...state, ...navigation, publicLogin };
  shell.navigationActions = actions;
  shell.navigation = input;
  void shell.whenRendered().then(() => {
    if (shell.disposed || shell.navigation !== input || state.kind !== 'member') return;
    const account = shell.navigationNode?.querySelector('tbc-account-menu');
    accountMenuCleanup = () => account?.close();
    void inboxSource.refreshChapters();
    startStream();
    void loadConversations();
    applyAccountStylesheet();
  });
}

function renderNavContents() {
  const user = getUser();
  if (!user) {
    stopStream();
    clearAccountLink();
    clearContinuumLink();
    const path = window.location.pathname;
    replaceNavigation(
      { kind: 'anonymous', onLogin: path === '/login', onRegister: path === '/register' },
      {
        register(event) {
          event.preventDefault();
          window.navigate('/register');
        }
      }
    );
    return;
  }

  if (needsEmailVerification()) {
    stopStream();
    closeConversationPopup();
    clearContinuumLink();
    applyAccountStylesheet();
    replaceNavigation({ kind: 'unverified' }, {});
    return;
  }

  const accountLabel = user.username || 'Account';
  const accountDestination = accountMenuDestination();
  replaceNavigation(
    {
      kind: 'member',
      homeHref: getCanonicalURL() || '/',
      isAdmin: !!user.is_admin,
      accountDestination,
      accountLabel,
      accountEmail: user.email || ''
    },
    {
      home(event) {
        if (event.currentTarget.host === location.host) {
          event.preventDefault();
          window.navigate('/');
        }
      },

      admin(event) {
        event.preventDefault();
        window.navigate('/admin');
      }
    }
  );
}

let admirePill = null;
let widthButton = null;
let admireAllowed = true;
let admireOpener = null;
let admirePreviousInert = false;

function renderViewControls() {
  admirePill = shell?.artworkButton;
  widthButton = shell?.widthButton;
  if (!admirePill || !widthButton) return;
  // The cover lives on two .body-cover-layer divs now, not on body's own
  // background-image — read the data flag lib/cover.js maintains instead.
  const hasCover = document.body.dataset.hasCover === 'true';
  if (isAdmiring() && (!hasCover || !admireAllowed)) {
    setAdmireMode(false);
    return;
  }
  shell.syncChrome();
  const artworkHidden = !hasCover || !admireAllowed;
  shell.artworkHidden = artworkHidden;
  renderAdmireAttribution();
}

function closeNavDisclosures(nav, focused = document.activeElement) {
  if (!nav) return;
  let restore;
  for (const name of ['account', 'msg', 'reviews']) {
    const menu = nav.querySelector(`#${name}-menu`);
    const trigger = nav.querySelector(`#${name}-menu-button`);
    if (!menu || menu.hidden) continue;
    if (menu.contains(focused)) {
      restore = trigger;
      focusElement(trigger);
    }
    if (name === 'account') nav.querySelector('tbc-account-menu')?.close();
    else nav.querySelector(name === 'msg' ? 'tbc-inbox-menu' : 'tbc-reviews-menu')?.close();
  }
  accountMenuCleanup?.();
  return restore;
}

function isAdmiring() {
  return shell?.admire || false;
}

let artworkFocusIntent = null;

function renderAdmireAttribution() {
  const credit = (document.body.dataset.attribution || '').trim();
  const assetURL = document.body.dataset.coverAssetUrl || '';
  const attribution =
    isAdmiring() && (credit || assetURL)
      ? {
          credit,
          assetURL,
          textColor: pickContrastingText(document.body.dataset.attributionColor || '#808080')
        }
      : null;
  const previous = shell.attribution;
  if (
    previous === attribution ||
    (previous &&
      attribution &&
      Object.keys(attribution).every((key) => previous[key] === attribution[key]))
  )
    return;
  shell.attribution = attribution;
}

function setAdmireMode(on) {
  if (!shell || shell.disposed) return;
  applyAdmireMode(on);
  return shell.whenRendered();
}

function applyAdmireMode(on) {
  const next = !!on;
  const main = document.getElementById('main-content');
  const wasOn = isAdmiring();
  if (next && !wasOn) {
    admireOpener = document.activeElement;
    admirePreviousInert = main?.inert || false;
    artworkFocusIntent = { open: true };
  } else if (!next && wasOn) {
    artworkFocusIntent = { open: false, opener: admireOpener };
    admireOpener = null;
  }
  shell.admire = next;
  shell.mainInert = next ? true : wasOn ? admirePreviousInert : main?.inert || false;
  shell.syncChrome();
  renderViewControls();
}

async function init() {
  const previous = document.getElementById('app');
  shell = document.createElement('tbc-app-shell');
  for (const attribute of previous.attributes) shell.setAttribute(attribute.name, attribute.value);
  shell.context = context;
  shell.onWidth = widthAction;
  shell.onArtwork = artworkAction;
  shell.onClosePopup = closeConversationPopup;
  shell.closeDisclosures = closeNavDisclosures;
  shell.onSkip = async (event) => {
    event.preventDefault();
    if (isAdmiring()) await setAdmireMode(false);
    const main = shell.main;
    for (const target of [...main.querySelectorAll('h1, h2'), main])
      if (focusElement(target)) break;
  };
  shell.onCommit = () => {
    const target = popupFocusReturn;
    popupFocusReturn = null;
    if (target && !hasOpenModal()) focusElement(target);
    const intent = artworkFocusIntent;
    artworkFocusIntent = null;
    if (!intent) return;
    const attribution = shell.attributionNode;
    const main = shell.main;
    if (intent.open) focusElement(attribution?.querySelector('a') || attribution || document.body);
    else
      focusElement(intent.opener) ||
        focusElement(main?.querySelector('h1, h2')) ||
        focusElement(main);
  };
  previous.replaceWith(shell);
  context.own(clearNavigationResources);
  context.own(() => shell.dispose());
  await shell.whenRendered();
  inboxSource = createInboxSource(context);

  window.addEventListener('keydown', (event) => {
    if (
      !isAdmiring() ||
      event.key !== 'Escape' ||
      event.defaultPrevented ||
      event.isComposing ||
      hasOpenModal() ||
      popupElement()?.contains(event.target) ||
      event.ctrlKey ||
      event.metaKey ||
      event.altKey ||
      event.shiftKey
    )
      return;
    event.preventDefault();
    void transitionContent(() => setAdmireMode(false));
  });

  context.listen(
    window,
    'click',
    (event) => {
      if (!isAdmiring() || event.target.closest?.('.admire-original-link')) return;
      // Consume the dismissal so it cannot also activate content beneath the artwork.
      event.preventDefault();
      event.stopImmediatePropagation();
      const generation = routeGeneration;
      void transitionContent(() => {
        if (generation === routeGeneration) return setAdmireMode(false);
      });
    },
    true
  );

  function artworkAction() {
    const generation = routeGeneration;
    void transitionContent(() => {
      if (generation === routeGeneration) return setAdmireMode(!isAdmiring());
    });
  }

  function widthAction() {
    const storage = accountStorage();
    void transitionContent(async () => {
      if (
        storage.userId !== getStorageAccountId() ||
        !document.documentElement.classList.contains('layout-lg')
      )
        return;
      const expanded = !document.documentElement.classList.contains('content-expanded');
      document.documentElement.classList.toggle('content-expanded', expanded);
      try {
        savePreference('content_expanded', expanded, storage);
      } catch {
        context.services.toasts.show({
          message: 'The width preference could not be saved. It applies to this page session.',
          type: 'warning'
        });
      }
      renderViewControls();
      await shell.whenRendered();
      focusElement(widthButton);
    });
  }

  const lifetime = context;
  lifetime.listen(window, 'body-cover-changed', renderViewControls);
  const stopAppearance = window.BardicAppearance?.subscribe(renderViewControls);
  if (stopAppearance) lifetime.onDispose(stopAppearance);

  renderViewControls();

  window.addEventListener('popstate', route);

  document.addEventListener('click', (e) => {
    if (e.defaultPrevented) return;
    if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    const a = e.target.closest?.('a[href]');
    if (!a) return;
    if (a.hasAttribute('download')) return;
    if (a.target && a.target !== '' && a.target !== '_self') return;
    const href = a.getAttribute('href');
    if (!href || !href.startsWith('/')) return;
    e.preventDefault();
    window.navigate(href);
  });

  // Boot-time auth: if there's a refresh cookie, mint an access token before
  // routing so views can rely on getUser()/getToken().
  await bootAuth();

  initImageReporting();

  // If the signed-in user is behind on the Code of Conduct, surface the
  // blocking modal before any view mounts. Routes still render underneath,
  // but the modal swallows interaction until the user agrees (or signs out).
  await maybeShowCoC();

  // If we're on a configured hostname, resolve the continuum and stash it so
  // route('/') can redirect to the continuum view and nav can hide cross-
  // continuum entries.
  hostContext = needsEmailVerification() ? null : await resolveContinuumHost(get);
  setContinuumHostContext(hostContext);

  // Private continuums on a hostname return 401 to anonymous callers.
  // Rewrite the URL to /login so route() mounts the login view; the user
  // returns here after signing in.
  if (hostContext?.authRequired && !getUser() && location.pathname === '/') {
    const next =
      location.pathname === '/login'
        ? new URLSearchParams(location.search).get('next') || '/'
        : location.pathname + location.search + location.hash;
    history.replaceState(null, '', '/login?next=' + encodeURIComponent(next));
    hostContext = null;
  }

  appReady = true;
  route();
}

async function renderAnonymousPage() {
  if (!appReady) return;
  closeConversationPopup();
  closeModals((card) => !!card.querySelector('form, input, textarea, .cm-editor'));

  const scrolls = [...document.querySelectorAll('#main-content, #main-content [id]')]
    .filter((node) => node.scrollTop || node.scrollLeft)
    .map((node) => [node.id, node.scrollTop, node.scrollLeft]);
  const generation = routeGeneration;
  try {
    const context = await resolveContinuumHost(get);
    if (generation !== routeGeneration || getUser()) return;
    hostContext = context;
    setContinuumHostContext(context);
  } catch {
    /* The existing public view can still attempt its own read. */
  }
  await route();
  for (const [id, top, left] of scrolls) {
    const node = document.getElementById(id);
    if (node) {
      node.scrollTop = top;
      node.scrollLeft = left;
    }
  }
}

async function refreshPublicLoginPage({ owner, context, url, restoreScroll }) {
  const isCurrent = () =>
    owner === getAuthOwner() && context === getAuthContext() && window.location.href === url;

  if (!isCurrent()) return;
  const generation = routeGeneration + 1;
  await route({ isCurrent });
  if (
    generation !== routeGeneration ||
    context !== getAuthContext() ||
    window.location.href !== url
  )
    return;
  restoreScroll(document.getElementById('main-content'));
}

installAuthRecovery({
  context,

  anonymous: () => {
    void renderAnonymousPage();
  },

  cancel: (attempt) => {
    if (attempt.intent !== 'public-login' || attempt.user) void renderAnonymousPage();
  },

  async resume(attempt) {
    const freshGet = async (path) => {
      assertAuthRecovery(attempt);
      const value = await get(path, { auth: 'bypass', signal: attempt.controller.signal });
      assertAuthRecovery(attempt);
      return value;
    };

    const context = needsEmailVerification() ? null : await resolveContinuumHost(freshGet);
    const required =
      window.location.pathname === '/' && context
        ? resourceRequiresAuth(context, context)
        : await loadPageAuthRequirement(window.location.pathname, freshGet);
    assertAuthRecovery(attempt);
    setPageAuthRequired(required);
    hostContext = context;
    setContinuumHostContext(context);
    renderNav();
  },

  home() {
    closeConversationPopup();
    closeModals();
    const canonical = getCanonicalURL();
    if (canonical && new URL(canonical).origin !== window.location.origin)
      window.location.assign(`${canonical}/`);
    else window.navigate('/', { replace: true });
  }
});

init();
